/**
 * Unified Analytics Helper
 *
 * Provides a single interface for tracking events across all analytics platforms
 * (PostHog, HubSpot, Google Analytics, Facebook Pixel).
 *
 * This ensures consistent tracking across platforms and makes it easier to add
 * or remove analytics providers without updating component code.
 *
 * Analytics services are lazy-loaded to reduce initial bundle size.
 * The posthog-js library and other analytics code is only loaded after user consent.
 *
 * @example
 * import { trackEvent } from '$lib/utils/analytics';
 *
 * trackEvent('button_clicked', {
 *   button_text: 'Sign Up',
 *   page: 'homepage'
 * });
 */

import { browser, dev } from "$app/environment";

// Cached module references for lazy-loaded analytics services
/** @type {typeof import('$lib/services/ga4.js') | null} */
let ga4Module = null;
/** @type {typeof import('$lib/services/posthog.js') | null} */
let postHogModule = null;
/** @type {typeof import('$lib/services/hubspot.js') | null} */
let hubSpotModule = null;

// Promise cache to prevent concurrent import operations
/** @type {Promise<{ga4Module: typeof import('$lib/services/ga4.js'), postHogModule: typeof import('$lib/services/posthog.js'), hubSpotModule: typeof import('$lib/services/hubspot.js')} | null> | null} */
let loadingPromise = null;

/**
 * Check if the user has given GDPR consent for analytics
 * Uses the beyonk_gdpr cookie set by the GDPR banner
 * @returns {boolean} True if analytics consent has been given
 */
function hasAnalyticsConsent() {
    if (!browser) return false;
    try {
        const cookie = document.cookie.split("; ").find((row) => row.startsWith("beyonk_gdpr="));
        if (!cookie) return false;
        // Cookie is base64-encoded, not URL-encoded
        const value = JSON.parse(atob(cookie.split("=")[1]));
        // Correct property path is choices.analytics
        return value?.choices?.analytics === true;
    } catch (error) {
        if (dev) {
            console.warn("[Analytics] Failed to parse GDPR consent cookie:", error);
        }
        return false;
    }
}

/**
 * Lazy-load analytics modules on first use
 * Only loads modules if GDPR consent has been given
 * This avoids bundling the heavy posthog-js library in the initial payload
 * Uses a promise cache to prevent concurrent import operations
 * @returns {Promise<{ga4Module: typeof import('$lib/services/ga4.js') | null, postHogModule: typeof import('$lib/services/posthog.js') | null, hubSpotModule: typeof import('$lib/services/hubspot.js') | null} | null>}
 */
async function loadAnalyticsModules() {
    // Don't load modules if consent hasn't been given yet
    if (!hasAnalyticsConsent()) {
        return null;
    }

    // Return cached modules if already loaded
    if (ga4Module && postHogModule && hubSpotModule) {
        return { ga4Module, postHogModule, hubSpotModule };
    }

    // Return existing loading promise to prevent concurrent imports
    if (loadingPromise) {
        return loadingPromise;
    }

    // Start loading and cache the promise
    loadingPromise = Promise.all([
        import("$lib/services/ga4.js"),
        import("$lib/services/posthog.js"),
        import("$lib/services/hubspot.js"),
    ]).then(([ga4, postHog, hubSpot]) => {
        ga4Module = ga4;
        postHogModule = postHog;
        hubSpotModule = hubSpot;
        return { ga4Module, postHogModule, hubSpotModule };
    });

    return loadingPromise;
}

/**
 * Track an event across all enabled analytics platforms
 *
 * @param {string} eventName - Name of the event
 * @param {Object} [properties={}] - Event properties
 *
 * @example
 * trackEvent('form_submitted', {
 *   form_name: 'contact',
 *   success: true
 * });
 */
export async function trackEvent(eventName, properties = {}) {
    if (!browser) return;

    try {
        const modules = await loadAnalyticsModules();
        if (!modules) return; // No consent yet, skip tracking

        // Track with GA4
        modules.ga4Module?.trackEvent(eventName, properties);

        // Track with PostHog
        modules.postHogModule?.captureEvent(eventName, properties);

        // Track with HubSpot
        modules.hubSpotModule?.trackEvent(eventName, properties);
    } catch (error) {
        // Silently fail - tracking errors shouldn't impact user experience
        if (dev) {
            console.error("[Analytics] Failed to track event:", error);
        }
    }
}

/**
 * Track a pageview across all enabled analytics platforms
 *
 * Note: This is handled automatically by the layout component's afterNavigate hook.
 * Only use this for manual pageview tracking in special cases.
 *
 * @param {string} [path] - Page path (defaults to current path)
 * @param {Object} [properties={}] - Additional properties
 * @param {{ skipGA4?: boolean }} [options={}] - Options to control which platforms receive the pageview
 * @returns {Promise<void>}
 */
export async function trackPageview(path, properties = {}, options = {}) {
    if (!browser) return;

    try {
        const modules = await loadAnalyticsModules();
        if (!modules) return; // No consent yet, skip tracking

        // Track with GA4 (skip when auto pageview already covers this page)
        if (!options.skipGA4) {
            modules.ga4Module?.trackPageview(path, undefined, properties);
        }

        // Track with PostHog
        modules.postHogModule?.capturePageview(path, properties);

        // Track with HubSpot
        modules.hubSpotModule?.trackPageview(path, properties);
    } catch (error) {
        // Silently fail - tracking errors shouldn't impact user experience
        if (dev) {
            console.error("[Analytics] Failed to track pageview:", error);
        }
    }
}

/**
 * Identify a user across all analytics platforms
 *
 * Call this when a user logs in or provides their email address.
 *
 * @param {string} email - User's email address
 * @param {Object} [properties={}] - Additional user properties
 * @returns {Promise<void>}
 *
 * @example
 * identifyUser('user@example.com', {
 *   firstname: 'John',
 *   lastname: 'Doe',
 *   company: 'Acme Corp'
 * });
 */
export async function identifyUser(email, properties = {}) {
    if (!browser) return;

    try {
        const modules = await loadAnalyticsModules();
        if (!modules) return; // No consent yet, skip tracking

        // Set user ID in GA4 (use email as user ID)
        modules.ga4Module?.setUserId(email);

        // Identify with PostHog
        modules.postHogModule?.identifyUser(email, properties);

        // Identify with HubSpot
        modules.hubSpotModule?.identifyUser(email, properties);
    } catch (error) {
        // Silently fail - tracking errors shouldn't impact user experience
        if (dev) {
            console.error("[Analytics] Failed to identify user:", error);
        }
    }
}

/**
 * Common event names for consistent tracking
 *
 * Using predefined event names helps maintain consistency across the application
 * and makes it easier to create reports in analytics platforms.
 */
export const EVENTS = {
    // Navigation
    MENU_OPENED: "menu_opened",
    MENU_CLOSED: "menu_closed",
    PAGE_VIEW: "page_view",

    // Forms
    FORM_STARTED: "form_started",
    FORM_SUBMITTED: "form_submitted",
    FORM_ERROR: "form_error",
    CONTACT_MODAL_OPENED: "contact_modal_opened",
    CONTACT_FORM_STARTED: "contact_form_started",
    CONTACT_FORM_SUBMITTED: "contact_form_submitted",

    // Buttons & CTAs
    BUTTON_CLICKED: "button_clicked",
    CTA_CLICKED: "cta_clicked",
    LINK_CLICKED: "link_clicked",

    // Media
    VIDEO_PLAYED: "video_played",
    VIDEO_PAUSED: "video_paused",
    VIDEO_COMPLETED: "video_completed",

    // Downloads
    FILE_DOWNLOADED: "file_downloaded",
    BROCHURE_DOWNLOADED: "brochure_downloaded",

    // Engagement
    NEWSLETTER_SIGNUP: "newsletter_signup",
    SCROLL_DEPTH: "scroll_depth",

    // E-commerce (if applicable)
    PRODUCT_VIEWED: "product_viewed",
    ADD_TO_CART: "add_to_cart",
    CHECKOUT_STARTED: "checkout_started",
    PURCHASE_COMPLETED: "purchase_completed",

    // Errors
    ERROR_OCCURRED: "error_occurred",
    FOUR_OH_FOUR: "404_error",
};
